import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiService, type MediaItem } from '../../../services/api';
import { useLocale } from '../../../locales';

interface MediaLibraryModalProps {
  onClose: () => void;
  onSelect: (mediaItem: MediaItem) => void;
  currentUrl?: string;
}

// Tree node types
interface FolderNode {
  name: string;
  type: 'folder';
  path: string;
  children: (FolderNode | FileNode)[];
}

interface FileNode {
  name: string;
  type: 'file';
  path: string;
  mediaItem: MediaItem;
}

type TreeNode = FolderNode | FileNode;

// Helper function to check if a node is a folder
const isFolder = (node: TreeNode): node is FolderNode => node.type === 'folder';

// Build tree structure from API response
const buildTreeFromData = (data: any, fallbackName: string): FolderNode[] => {
  const roots: FolderNode[] = [];

  // Process rootImages (root level image files)
  if (data.rootImages && Array.isArray(data.rootImages)) {
    const imageFiles = data.rootImages.filter((item: MediaItem) => item.type === 'image' || !item.type);
    if (imageFiles.length > 0) {
      roots.push({
        name: 'rootImages',
        type: 'folder',
        path: 'rootImages',
        children: imageFiles.map((item: MediaItem) => ({
          name: item.name || item.file_name || fallbackName,
          type: 'file',
          path: `rootImages/${item.id || item.url}`,
          mediaItem: item,
        })),
      });
    }
  }

  // Process nested categories (e.g., klasorler/images)
  Object.keys(data).forEach((key) => {
    if (key === 'rootImages') return;

    const categoryData = data[key];

    if (typeof categoryData === 'object' && categoryData !== null && !Array.isArray(categoryData)) {
      // This is a nested category like "klasorler": { "images": [...] }
      const folder: FolderNode = {
        name: key,
        type: 'folder',
        path: key,
        children: [],
      };

      Object.keys(categoryData).forEach((subKey) => {
        const subData = categoryData[subKey];

        // Check for "images" key which contains the image files
        if (subKey === 'images' && Array.isArray(subData)) {
          const imageFiles = subData.filter((item: MediaItem) => item.type === 'image' || !item.type);
          const subFolder: FolderNode = {
            name: subKey,
            type: 'folder',
            path: `${key}/${subKey}`,
            children: imageFiles.map((item: MediaItem) => ({
              name: item.name || item.file_name || fallbackName,
              type: 'file',
              path: `${key}/${subKey}/${item.id || item.url}`,
              mediaItem: item,
            })),
          };
          folder.children.push(subFolder);
        }
      });

      if (folder.children.length > 0) {
        roots.push(folder);
      }
    }
  });

  return roots;
};

const MediaLibraryModal: React.FC<MediaLibraryModalProps> = ({
  onClose,
  onSelect,
  currentUrl,
}) => {
  const { t } = useLocale();
  const contentRef = useRef<HTMLDivElement>(null);
  const [treeData, setTreeData] = useState<FolderNode[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPath, setCurrentPath] = useState<string[]>([]);

  // Navigate to a folder by path
  const navigateToFolder = useCallback((path: string[]) => {
    setCurrentPath(path);
  }, []);

  const handleSelect = useCallback(
    (item: MediaItem) => {
      onSelect(item);
      onClose();
    },
    [onSelect, onClose]
  );

  const handleFolderClick = useCallback((folder: FolderNode) => {
    navigateToFolder([...currentPath, folder.name]);
  }, [currentPath, navigateToFolder]);

  useEffect(() => {
    contentRef.current?.focus();
  }, []);

  // Load saved folder path from localStorage on mount
  useEffect(() => {
    const savedPath = localStorage.getItem('media-library:last-path');
    if (savedPath) {
      try {
        const parsedPath = JSON.parse(savedPath);
        setCurrentPath(parsedPath);
      } catch {
        // Invalid JSON, ignore
      }
    }
  }, []);

  // Save current folder path to localStorage whenever it changes
  useEffect(() => {
    if (currentPath.length > 0) {
      localStorage.setItem('media-library:last-path', JSON.stringify(currentPath));
    } else {
      localStorage.removeItem('media-library:last-path');
    }
  }, [currentPath]);

  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // If we're in a subfolder, go back; otherwise close modal
        if (currentPath.length > 0) {
          navigateToFolder(currentPath.slice(0, -1));
        } else {
          onClose();
        }
      }
    };

    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, [onClose, currentPath, navigateToFolder]);

  useEffect(() => {
    const fetchMedia = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response: any = await (apiService as any).request('/media-library/all-media');

        if (response?.data) {
          const tree = buildTreeFromData(response.data, t("mediaLibrary.fallbackName"));
          setTreeData(tree);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : t("mediaLibrary.loadError"));
      } finally {
        setIsLoading(false);
      }
    };

    fetchMedia();
  }, []);

  // Get the current folder contents
  const getCurrentContents = useCallback((): { folders: FolderNode[]; files: FileNode[] } => {
    let currentNodes: TreeNode[] = treeData;

    // Navigate through the path
    for (const pathSegment of currentPath) {
      const folder = currentNodes.find(
        (node) => isFolder(node) && node.name === pathSegment
      ) as FolderNode | undefined;
      if (folder) {
        currentNodes = folder.children;
      }
    }

    // Separate folders and files
    const folders = currentNodes.filter(isFolder);
    const files = currentNodes.filter((node) => !isFolder(node)) as FileNode[];

    return { folders, files };
  }, [treeData, currentPath]);

  const { folders, files } = getCurrentContents();

  return (
    <div className="qe-modal" role="presentation">
      <div className="qe-modal__backdrop" onClick={onClose} />
      <div className="qe-modal__content qe-modal__content--media-library" ref={contentRef} tabIndex={-1}>
        <div className="qe-modal__header">
          <div className="qe-modal__header-content">
            <h2 className="qe-modal__title">{t("mediaLibrary.title")}</h2>
            <p className="qe-modal__subtitle">{t("mediaLibrary.subtitle")}</p>
          </div>
          <button
            type="button"
            className="qe-button qe-button--ghost qe-button--icon"
            onClick={onClose}
            aria-label={t("mediaLibrary.close")}
          >
            ✕
          </button>
        </div>

        <div className="qe-modal__body">
          {isLoading ? (
            <div className="qe-media-library__loading">
              <p>{t("mediaLibrary.loading")}</p>
            </div>
          ) : error ? (
            <div className="qe-media-library__error">
              <p>{error}</p>
            </div>
          ) : treeData.length === 0 ? (
            <div className="qe-media-library__empty">
              <p>{t("mediaLibrary.empty")}</p>
            </div>
          ) : (
            <>
              {/* Breadcrumb Navigation */}
              <div className="qe-file-browser__breadcrumb">
                <button
                  className="qe-file-browser__breadcrumb-item"
                  onClick={() => navigateToFolder([])}
                >
                  🏠 {t("mediaLibrary.rootFolder")}
                </button>
                {currentPath.map((segment, index) => (
                  <React.Fragment key={segment}>
                    <span className="qe-file-browser__breadcrumb-separator">/</span>
                    <button
                      className="qe-file-browser__breadcrumb-item"
                      onClick={() => navigateToFolder(currentPath.slice(0, index + 1))}
                    >
                      {segment}
                    </button>
                  </React.Fragment>
                ))}
              </div>

              {/* Current Folder Contents */}
              <div className="qe-file-browser__content">
                {folders.length === 0 && files.length === 0 ? (
                  <div className="qe-file-browser__empty">
                    <p>{t("mediaLibrary.folderEmpty")}</p>
                  </div>
                ) : (
                  <>
                    {/* Folders */}
                    {folders.map((folder) => (
                      <div
                        key={folder.path}
                        className="qe-file-browser__item qe-file-browser__item--folder"
                        onClick={() => handleFolderClick(folder)}
                        onDoubleClick={() => handleFolderClick(folder)}
                      >
                        <span className="qe-file-browser__icon">📁</span>
                        <span className="qe-file-browser__name">{folder.name}</span>
                        <span className="qe-file-browser__count">({folder.children.length})</span>
                        <span className="qe-file-browser__arrow">→</span>
                      </div>
                    ))}

                    {/* Image Files - Grid Display */}
                    <div className="qe-media-library__grid">
                      {files.map((file) => {
                        const isSelected = currentUrl === file.mediaItem.url;
                        return (
                          <div
                            key={file.path}
                            className={`qe-media-library__item ${isSelected ? 'qe-media-library__item--selected' : ''}`}
                            onClick={() => handleSelect(file.mediaItem)}
                          >
                            <div className="qe-media-library__item-image">
                              <img
                                src={file.mediaItem.thumbnail || file.mediaItem.url}
                                alt={file.name}
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.style.display = 'none';
                                }}
                              />
                            </div>
                            <div className="qe-media-library__item-name" title={file.name}>
                              {file.name}
                            </div>
                            {file.mediaItem.size_formatted && (
                              <div className="qe-media-library__item-size">
                                {file.mediaItem.size_formatted}
                              </div>
                            )}
                            {isSelected && (
                              <div className="qe-media-library__item-check">
                                <svg
                                  width="20"
                                  height="20"
                                  viewBox="0 0 20 20"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M16.667 5L7.5 14.167 3.333 10"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>

        <div className="qe-modal__footer">
          {currentPath.length > 0 && (
            <button
              type="button"
              className="qe-button qe-button--secondary"
              onClick={() => navigateToFolder(currentPath.slice(0, -1))}
            >
              {t("mediaLibrary.back")}
            </button>
          )}
          <button
            type="button"
            className="qe-button qe-button--primary"
            onClick={onClose}
          >
            {t("mediaLibrary.cancel")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MediaLibraryModal;

